/**
 * SubscribeModal — subscription confirmation dialog
 *
 * Replaces cycle-1 console-log stub. Renders a modal overlay with three phases:
 *   idle/submitting → confirm screen
 *   success         → confirmation screen
 *   error           → retry screen
 *
 * Uses useSubscription hook (POST /api/broker/v1/subscriptions).
 *
 * Refs: DOPLA-602, design-contract §11 (Binding Lifecycle, order variant)
 */

import { useEffect, type MouseEvent } from 'react';
import type { TileConfig } from '../config/schema';
import { useAuth } from '../auth/KeycloakProvider';
import { useSubscription } from '../hooks/useSubscription';
import { t } from '../i18n';

interface SubscribeModalProps {
  tile: TileConfig;
  onClose: () => void;
}

export default function SubscribeModal({ tile, onClose }: SubscribeModalProps) {
  const { token } = useAuth();
  const { state, trigger, reset } = useSubscription(tile.id, token);

  // Close on Escape key
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'submitting') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state, onClose]);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && state !== 'submitting') onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-modal-title"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-8)',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--border-default)',
          width: '100%',
          maxWidth: '440px',
          padding: 'var(--sp-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {(state === 'idle' || state === 'submitting') && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow">{t('subscribeModal.eyebrow')}</p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {/* Inline substitution — avoids a runtime t() template engine */}
                {t('subscribeModal.title').replace('{{service}}', tile.title)}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.body')}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button
                className="dp-btn dp-btn--ghost"
                onClick={onClose}
                disabled={state === 'submitting'}
              >
                {t('destructive.cancel')}
              </button>
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => void trigger()}
                disabled={state === 'submitting'}
                style={{ opacity: state === 'submitting' ? 0.7 : 1 }}
              >
                {state === 'submitting'
                  ? t('subscribeModal.submitting')
                  : t('subscribeModal.confirm')}
              </button>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow">{t('subscribeModal.successEyebrow')}</p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {t('subscribeModal.successTitle')}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.successBody')}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--primary" onClick={onClose}>
                {t('subscribeModal.done')}
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <p className="dp-eyebrow" style={{ color: 'var(--dp-crimson-600)' }}>
                {t('subscribeModal.errorEyebrow')}
              </p>
              <h2
                id="subscribe-modal-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                }}
              >
                {t('subscribeModal.errorTitle')}
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  color: 'var(--fg-3)',
                  lineHeight: 'var(--lh-body-sm)',
                  margin: 0,
                }}
              >
                {t('subscribeModal.errorBody')}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--ghost" onClick={onClose}>
                {t('destructive.cancel')}
              </button>
              <button
                className="dp-btn dp-btn--primary"
                onClick={() => { reset(); void trigger(); }}
              >
                {t('subscribeModal.retry')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
